import { RolesTenantsMappings } from '@components/Authorization';
import { LoadingButton } from '@mui/lab';
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Link as MUILink,
	TextField,
	Typography
} from '@mui/material';
import { AnalyticsActionName } from '@utils/analytics';
import { emailValidationScheme } from '@utils/validation';
import { APP_ROUTES } from 'constants/routes';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import useAnalytics from 'hooks/useAnalytics';
import { compact } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { IDialogProps } from 'types/common';
import { IInviteUsers, InviteFunc } from 'types/users';
import { array, object } from 'yup';

interface IUserInviteDialog extends IDialogProps<IInviteUsers> {
	adminInvite: boolean;
	inviteUrl?: string;
	inviteFunc: InviteFunc;
}

const UserInviteDialog: React.FC<IUserInviteDialog> = ({
	onCancel,
	onSubmit,
	adminInvite,
	inviteUrl,
	inviteFunc
}) => {
	const { invokeToastableAction } = useToast();
	const track = useAnalytics();

	const formik = useFormik({
		initialValues: {
			users: [],
			mappings: []
		},
		validationSchema: object({
			users: array()
				.of(emailValidationScheme())
				.required(intl.tr('validations.field.required'))
				.min(1, intl.tr('validations.field.required'))
		}),
		onSubmit: async (values) => {
			await invokeToastableAction(
				async () => {
					await inviteFunc(values);

					if (adminInvite) {
						track(AnalyticsActionName.inviteAdminUsers, values);
					}
				},
				'users.invite.dialog.save',
				onSubmit
			);
		}
	});

	const userErrors = formik.errors.users;
	const usersHelperText = Array.isArray(userErrors)
		? compact(userErrors) && compact(userErrors)[0]
		: userErrors;
	const touched = formik.touched.users;

	const titleId = adminInvite
		? 'users.invite.admins.dialog.title'
		: 'users.invite.dialog.title';

	const disabled = !adminInvite && !inviteUrl;

	return (
		<Dialog open disableRestoreFocus maxWidth="md">
			<DialogTitle>{intl.tr(titleId)}</DialogTitle>
			<DialogContent>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						{disabled && (
							<Alert
								severity="warning"
								sx={{
									marginBottom: 2,
									display: {
										xs: 'none',
										sm: 'flex'
									}
								}}
								data-testid="missing-invite-alert"
							>
								<Box display="flex" flexDirection="row">
									{intl.tr('users.invite.dialog.input.missing.url')}
									<Link
										to={APP_ROUTES.projectSettings}
										data-testid="missing-invite-alert-link"
									>
										<MUILink
											component="span"
											paddingLeft={0.1}
											paddingRight={0.3}
										>
											{intl.tr('users.invite.dialog.input.project.settings')}
										</MUILink>
									</Link>
									{intl.tr('users.invite.dialog.input.missing.url.page')}
								</Box>
							</Alert>
						)}
						{!adminInvite && (
							<Typography pb={1}>
								{intl.tr('users.invite.dialog.input.users.title')}
							</Typography>
						)}
						<Autocomplete
							data-testid="users-invite-autocomplete"
							multiple
							filterSelectedOptions
							id="users"
							options={[]}
							sx={{ pt: 1 }}
							freeSolo
							autoSelect
							ChipProps={{
								variant: 'outlined'
							}}
							onChange={(_, v) => {
								formik.setFieldValue('users', v);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									required
									autoFocus
									label={intl.tr('users.invite.dialog.input.users.label')}
									fullWidth
									error={touched && Boolean(usersHelperText)}
									helperText={touched && usersHelperText}
								/>
							)}
						/>
					</Grid>
					{!adminInvite && (
						<>
							<Grid item xs={12}>
								<Typography pt={3} pb={1}>
									{intl.tr('dialog.authorization.header')}
								</Typography>
								<RolesTenantsMappings formik={formik} id="mappings" />
							</Grid>
							{formik.values.mappings && (
								<Grid item xs={12} pt={2}>
									<Alert severity="info">
										{intl.tr('users.invite.dialog.alert.content')}
									</Alert>
								</Grid>
							)}
						</>
					)}
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onCancel}
					data-testid="users-invite-dialog-cancel-button"
					sx={{ mr: 1 }}
				>
					{intl.tr('button.cancel')}
				</Button>
				<Box
					title={
						disabled
							? intl.tr('users.invite.dialog.input.missing.url.tooltip')
							: ''
					}
				>
					<LoadingButton
						variant="contained"
						color="primary"
						loading={formik.isSubmitting}
						onClick={() => formik.handleSubmit()}
						disabled={disabled}
						data-testid="users-invite-approve-button"
					>
						{intl.tr('button.create')}
					</LoadingButton>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

UserInviteDialog.defaultProps = {
	inviteUrl: ''
};

export default UserInviteDialog;
